import ConverterListModel from '@client/models/converter'
import { getProgressTasks, TaskView } from '@client/stores/progressStore'
import { refreshProgress } from '@client/stores/progressWatch'
import { isActive } from './convertQueue'

//
// The conversions of one person that still wait or run, across projects.
//
// Signing out, reloading the page and disabling an account ask what is to
// become of them (operator, 17.10.2026, F3). Without an answer they run on.
//

const converterListModel = new ConverterListModel()

/**
 * Asks the server first, so that the answer does not depend on which views
 * were open — for every conversion of the tenant still waiting or running,
 * not for the newest tasks, among which a busy tenant pushes them out.
 */
export async function findActiveConversionsOf(userId: string): Promise<TaskView[]> {
    if (!userId) return []

    // Only what the server names now: a task whose end the store missed
    // would otherwise ask about something that no longer runs.
    const active = await refreshProgress({ kind: 'convert', active: true })
    if (!active) return []
    const ids = new Set(active.map((entry) => String(entry?.id ?? '')))
    const tasks = getProgressTasks()

    return [...ids].map((id) => tasks[id]).filter((task) => !!task && isActive(task) && task.createdBy === userId)
}

/** What the store knows right now — for the moment a page unloads, when nothing can be awaited. */
export const knownActiveConversionsOf = (userId: string): TaskView[] =>
    Object.values(getProgressTasks()).filter((task) => task.kind === 'convert' && isActive(task) && task.createdBy === userId)

/**
 * Cancels one's own conversions, project by project; returns why a project
 * refused, if one did.
 */
export async function cancelOwnConversions(tasks: TaskView[]): Promise<string[]> {
    const projects = [...new Set(tasks.map((task) => task.projectId).filter(Boolean))]
    const answers = await Promise.all(projects.map((projectId) => converterListModel.cancelAllConverts(projectId)))

    return answers.map((answer) => answer.reason || '').filter(Boolean)
}
